import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@frontierag/frontier-ui-library';
import { CROP_VIRTUAL_PRODUCT_TYPE, MAX_CROP_QUANTITY_LIMIT } from 'appConstants';
import { ProductRefChip } from 'components';
import QuantityControl from 'components/QuantityControl';
import type { IVirtualPack } from 'contexts/Products';
import type { IGenericObject } from 'types';
import type { ICropFormikProduct } from 'types/product';
import {
  calculateCropQuantityFromPacks,
  getCropPackSize,
  getCropProductQuantity,
  getCropProductTypeLabel,
} from 'utils';

export default function useCardFields(
  onQuantityChange: (quantity: number, productId: number) => void,
  onPacksChange: (packs: number, productId: number) => void,
) {
  const { t } = useTranslation('CreateCropOrder', { keyPrefix: 'viewBasket' });

  const getContainsProducts = useCallback(
    ({ packProducts = [] }: IVirtualPack | IGenericObject) => (
      <Box mb={-0.5}>
        {packProducts.map(({ packagedItemDescription, packageDescription }) => (
          <Box mb={0.5} key={packagedItemDescription} display={'flex'} gap={1}>
            <Box
              component="span"
              width={4}
              height={4}
              minWidth={4}
              minHeight={4}
              borderRadius={'50%'}
              bgcolor={'primary.main'}
              mt={1}
            />
            {packagedItemDescription} ({packageDescription})
          </Box>
        ))}
      </Box>
    ),
    [],
  );

  const getFields = useCallback(
    (product: ICropFormikProduct, productId: number) => {
      const productQuantity = getCropProductQuantity(product) || 0;
      const quantityStep = getCropPackSize(product) || 1;
      const maxQuantity =
        calculateCropQuantityFromPacks(MAX_CROP_QUANTITY_LIMIT, product) || MAX_CROP_QUANTITY_LIMIT;

      return [
        {
          label: t('card.productType'),
          value: getCropProductTypeLabel(product.productType),
        },
        {
          label: t('card.referenceCode'),
          value: <ProductRefChip productRef={product.packagedItemReference} />,
        },
        ...(product.productType === CROP_VIRTUAL_PRODUCT_TYPE
          ? [
              {
                label: t('card.contains'),
                value: getContainsProducts(product.productData),
              },
            ]
          : []),

        {
          label: t('card.numberOfPacks'),
          value: (
            <QuantityControl
              quantity={product.packagedItemQty}
              onChange={(packs) => onPacksChange(packs, productId)}
              minQuantity={1}
              maxQuantity={MAX_CROP_QUANTITY_LIMIT}
              step={1}
              testIdPrefix={`quantity-control-packs-${productId}`}
            />
          ),
        },
        {
          label: t('card.quantity'),
          value: (
            <QuantityControl
              quantity={productQuantity}
              onChange={(quantity) => onQuantityChange(quantity, productId)}
              minQuantity={quantityStep}
              maxQuantity={maxQuantity}
              step={quantityStep}
              testIdPrefix={`quantity-control-quantity-${productId}`}
            />
          ),
        },
      ];
    },
    [getContainsProducts, onPacksChange, onQuantityChange, t],
  );

  return { getFields };
}
